import { datef } from "@caredna-marlin/utils/dates";
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Dictionary, sortBy, startCase } from "lodash";
import { useState } from "react";
import CheckboxSelect from "~/components/common/forms/CheckboxSelect";
import DatePicker from "~/components/common/forms/DatePicker";
import { FilterChip } from "~/components/common/forms/FilterChip";
import FilterGridRow from "~/components/scheduler/FilterGridRow";
import { AvailabilityByTimespanQuery } from "~/graphql/__generated__/graphql";
import useDefaultServiceLines from "~/hooks/useDefaultServiceLines";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

export type ProviderFilters = {
  serviceLineIds: number[],
  providerTypeIds: number[],
  teamIds: number[],
  workingOn: Date | null,
  notWorkingOn: Date | null,
}

export const useDefaultFilters = () => {
  const serviceLineIds = useDefaultServiceLines()
  return {
    serviceLineIds,
    providerTypeIds: [],
    teamIds: [],
    workingOn: null,
    notWorkingOn: null,
  }
}

type Props = {
  values: ProviderFilters,
  teams: Dictionary<AvailabilityByTimespanQuery["team"][number]>,
  setValues: (newVal: ProviderFilters) => void,
}

export default function ProviderFilterChips({
  values,
  teams,
  setValues,
}: Props) {
  const { serviceLines, providerTypes } = useOrgConfig();

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const slLabel = values.serviceLineIds.map((id) => serviceLines[id]?.name).filter(Boolean).join(", ");
  const ptLabel = values.providerTypeIds.map((id) => startCase(providerTypes[id]?.name)).filter(Boolean).join(", ");
  const teamLabel = values.teamIds.map(id => teams[id]?.name).filter(Boolean).join(", ");

  return (
    <Box display="flex" flexWrap="wrap" gap={1} sx={{ m: 1 }}>
      {/* Categories chip */}
      <FilterChip
        selected={values.serviceLineIds.length > 0}
        label={slLabel}
        emptyLabel="Any Service Line"
        onClick={() => setFilterDialogOpen(true)}
        onDelete={() => setValues({ ...values, serviceLineIds: [] })}
      />

      <FilterChip
        selected={values.providerTypeIds.length > 0}
        label={ptLabel}
        emptyLabel="Any provider type"
        onClick={() => setFilterDialogOpen(true)}
        onDelete={() => setValues({ ...values, providerTypeIds: [] })}
      />
      {Object.values(teams).length > 0 && <FilterChip
        selected={values.teamIds.length > 0}
        label={teamLabel}
        emptyLabel="Any team"
        onClick={() => setFilterDialogOpen(true)}
        onDelete={() => setValues({ ...values, teamIds: [] })}
      />}
      {(values.workingOn || !values.notWorkingOn) && <FilterChip
        selected={!!values.workingOn}
        label={`Working ${values.workingOn ? datef(values.workingOn, "MM/dd/yyyy") : "or off"}`}
        emptyLabel="Any schedule"
        onClick={() => setFilterDialogOpen(true)}
        onDelete={() => setValues({ ...values, workingOn: null })}
      />}
      {values.notWorkingOn && <FilterChip
        selected={!!values.notWorkingOn}
        label={`Not working ${datef(values.notWorkingOn, "MM/dd/yyyy")}`}
        emptyLabel="Any schedule"
        onClick={() => setFilterDialogOpen(true)}
        onDelete={() => setValues({ ...values, notWorkingOn: null })}
      />}

      {filterDialogOpen && <ProviderFilterDialog
        onClose={() => setFilterDialogOpen(false)}
        onSave={setValues}
        values={values}
        teams={teams}
      />}
    </Box>
  );
}

type DialogProps = {
  onClose: () => void,
  onSave: (values: ProviderFilters) => void,
  teams: Dictionary<AvailabilityByTimespanQuery["team"][number]>,
  values: ProviderFilters,
}

const ProviderFilterDialog = ({ onClose, onSave, values, teams }: DialogProps) => {
  const { serviceLines, providerTypes } = useOrgConfig();
  const [local, setLocal] = useState(values);

  return <Dialog
      open={true}
      onClose={onClose}
      slotProps={{
        paper: {
          component: "form",
          sx: { p: 2, width: 640, maxWidth: "95vw" },
        },
      }}
    >
    <DialogContent>
      <Grid container spacing={2}>
        <FilterGridRow label="Service Lines">
          <CheckboxSelect
            fullWidth
            size="small"
            value={local.serviceLineIds}
            onChange={(serviceLineIds) => setLocal({ ...local, serviceLineIds})}
            options={Object.values(serviceLines).filter(sl => sl !== undefined).map(sl => ({ key: sl.id, label: sl.name }))}
          />
        </FilterGridRow>

        <FilterGridRow label="Provider Types">
          <CheckboxSelect
            fullWidth
            size="small"
            value={local.providerTypeIds}
            onChange={(providerTypeIds) => setLocal({ ...local, providerTypeIds})}
            options={Object.values(providerTypes).map(pt => ({ key: pt.id, label: startCase(pt.name) }))}
          />
        </FilterGridRow>

        {Object.values(teams).length > 0 && <FilterGridRow label="Provider Teams">
          <CheckboxSelect
            fullWidth
            size="small"
            value={local.teamIds}
            onChange={(teamIds) => setLocal({ ...local, teamIds})}
            options={sortBy(Object.values(teams).map(pt => ({ key: pt.id, label: pt.name })), "label")}
          />
        </FilterGridRow>}

        <FilterGridRow label="Working on day">
          <DatePicker
            slotProps={{ field: { clearable: true }, textField: { size: "small" } }}
            value={local.workingOn}
            sx={{ width: "100%" }}
            onChange={(workingOn) => workingOn?.getTime() && setLocal({...local, workingOn})}
          />
        </FilterGridRow>

        <FilterGridRow label="Not working on">
          <DatePicker
            slotProps={{ field: { clearable: true }, textField: { size: "small" } }}
            value={local.notWorkingOn}
            sx={{ width: "100%" }}
            onChange={(notWorkingOn) => notWorkingOn?.getTime() && setLocal({...local, notWorkingOn})}
          />
        </FilterGridRow>
      </Grid>
    </DialogContent>

    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        variant="contained"
        onClick={() => {
          onSave(local);
          onClose();
        }}
      >
        Apply
      </Button>
    </DialogActions>
  </Dialog>
}
